import EventTypes from "@wesstron/utils/Api/constants/eventTypes";
import moment from "moment";
import React, {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import EstimatedWeight from "../../../beans/estimated-weight/EstimatedWeight";
import {getOtherWeightsSelector} from "../../../selectors/settingsSelector";
import {utcDateFormatter, utcMomentToLocal} from "../../../utils/DateTimeUtils";
import {isBetween} from "../../../utils/MathUtils";
import {getAnimalUnit} from "../../../utils/SettingsUtils";
import {convertWeightUnitTo, getUnit} from "../../../utils/UnitUtils";
import Chart from "../../basics/chart/Chart";
import CompareWeightsTooltip from "../../charts/CompareWeightsTooltip";
import {calculateAutoDomainForWeightingChart} from "../ideal-charts/charts/utils";
import {resolveColorHexByNameWithWCAG} from "../../../utils/ColorUtils";
import {filterMap} from "../../../utils/Utils";
import {makeCustomTooltip} from "../ideal-charts/charts/makeCustomTooltip";
import {isDarkMode} from "../../../utils/DOMUtils";

const emptyArray = [];

function WeightingChart({
    weightDict,
    birthTime,
    start,
    end,
    chartDomain = emptyArray,
    referenceLines,
    showAverageWeightOnly = false,
}) {
    const {t} = useTranslation();

    const animalUnit = useMemo(() => getAnimalUnit(), []);

    const weightUnit = useMemo(
        () => getUnit("weight", animalUnit),
        [animalUnit]
    );

    const weightFormatter = useCallback(
        (value) => {
            if (value === undefined || value === null || isNaN(value))
                return null;
            return convertWeightUnitTo(value, {
                showUnit: false,
                unit: animalUnit,
                rawValue: true,
                fixed: 2,
            });
        },
        [animalUnit]
    );

    const weightArray = useSelector(getOtherWeightsSelector);

    const dataSet = useMemo(() => {
        const ds = [];
        const [chartStart, chartEnd] = chartDomain;
        const diff = Math.abs(
            moment.utc(chartStart).diff(moment.utc(chartEnd), "day")
        );
        const estimate = new EstimatedWeight(0, []);
        estimate.setWeightTable(weightArray, 1200);
        estimate.setBirthDate(birthTime);
        const allowedEvents = [
            EventTypes.WEIGHTING,
            EventTypes.INSERTION,
            `${EventTypes.WEIGHTING}_AT`,
        ];
        for (let i = 0; i <= diff; i++) {
            const dayUTC = moment.utc(chartStart).add(i, "day");
            const dayLOCAL = utcMomentToLocal(dayUTC);
            const estimated = estimate.getWeightByDate(+dayLOCAL);
            const isGroupActive = isBetween(+dayUTC, start, end);
            if (!isGroupActive) {
                ds.push({
                    time: +dayUTC,
                    estimated,
                });
                continue;
            }
            // getting events by utc day from dict
            const eventsForCurrentDay = weightDict[+dayUTC] ?? null;

            let eventsAdded = false;

            if (Array.isArray(eventsForCurrentDay)) {
                let mass = 0;
                let count = 0;
                for (const event of eventsForCurrentDay) {
                    if (showAverageWeightOnly) {
                        if (
                            allowedEvents.some((eventType) =>
                                event.hasOwnProperty(eventType)
                            )
                        ) {
                            count += event.AnmCnt ?? 0;
                            mass += (event.weight ?? 0) * (event.AnmCnt ?? 0);
                        }
                    } else {
                        const eventTypeKey = Object.values(EventTypes).find(
                            (key) => key in event
                        );
                        event[`${eventTypeKey}_AnmCnt`] = event.AnmCnt;
                        ds.push({
                            ...event,
                            time: +dayUTC,
                            estimated,
                        });
                    }
                    eventsAdded = true;
                }
                if (count) {
                    ds.push({
                        weight: mass / count,
                        time: +dayUTC,
                        estimated,
                    });
                } else {
                    eventsAdded = false;
                }
            }

            if (!eventsAdded) {
                ds.push({
                    time: +dayUTC,
                    estimated,
                });
            }
        }
        return ds;
    }, [
        chartDomain,
        birthTime,
        start,
        end,
        weightArray,
        weightDict,
        showAverageWeightOnly,
    ]);

    const chartDomainY = useMemo(() => {
        return calculateAutoDomainForWeightingChart(dataSet);
    }, [dataSet]);

    const dataDef = useMemo(() => {
        if (showAverageWeightOnly) {
            return [
                {
                    dataKey: "estimated",
                    color: "purple",
                    strokeDasharray: "5 5",
                    strokeWidth: 4,
                    type: "linear",
                    chartType: "Line",
                    unit: weightUnit,
                    name: t("expectedWeight"),
                    yAxisId: "lineAxis",
                    formatter: weightFormatter,
                },
                {
                    dataKey: "weight",
                    color: "blue",
                    chartType: "Line",
                    opacity: 1,
                    type: "linear",
                    strokeWidth: 2,
                    unit: weightUnit,
                    name: t("averageWeight"),
                    yAxisId: "scatterAxis",
                    formatter: weightFormatter,
                },
                {
                    dataKey: "weight",
                    color: "blue",
                    strokeWidth: 0,
                    type: "linear",
                    chartType: "Label",
                    unit: weightUnit,
                    yAxisId: "scatterAxis",
                    name: t("showLabelFor", {label: "$t(averageWeight)"}),
                    connectNulls: true,
                    formatter: weightFormatter,
                },
            ];
        }
        return [
            {
                dataKey: "estimated",
                color: "purple",
                strokeDasharray: "5 5",
                type: "linear",
                chartType: "Line",
                unit: weightUnit,
                name: t("expectedWeight"),
                yAxisId: "lineAxis",
                formatter: weightFormatter,
            },
            {
                dataKey: EventTypes.WEIGHTING,
                color: "blue",
                chartType: "Scatter",
                opacity: 1,
                unit: weightUnit,
                name: t("weighting"),
                yAxisId: "scatterAxis",
                formatter: weightFormatter,
            },
            // auto-weighting was disabled (#12270 for details)
            // {
            //     dataKey: `${EventTypes.WEIGHTING}_AT`,
            //     color: "teal",
            //     chartType: "Scatter",
            //     opacity: 1,
            //     unit: weightUnit,
            //     name: t("weightingAutomatic"),
            //     yAxisId: "scatterAxis",
            //     formatter: weightFormatter,
            // },
            {
                dataKey: EventTypes.SELL,
                color: "green",
                chartType: "Scatter",
                opacity: 1,
                unit: weightUnit,
                name: t("animalDocuments.sellGrid"),
                yAxisId: "scatterAxis",
                formatter: weightFormatter,
            },
            {
                dataKey: EventTypes.FALL,
                color: "red",
                chartType: "Scatter",
                opacity: 1,
                unit: weightUnit,
                name: t("menu.fall"),
                yAxisId: "scatterAxis",
                formatter: weightFormatter,
            },
            {
                dataKey: EventTypes.INSERTION,
                color: "orange",
                chartType: "Scatter",
                opacity: 1,
                unit: weightUnit,
                name: t("insertion"),
                yAxisId: "scatterAxis",
                formatter: weightFormatter,
            },
        ];
    }, [t, weightFormatter, weightUnit, showAverageWeightOnly]);

    const darkMode = isDarkMode();

    // This prepares a flattened dataset for the tooltip,
    // enabling it to display multiple values that share the same X coordinate (time).
    // For each data point and each defined dataKey present in that point,
    // it creates a separate tooltip entry enriched with display properties like color, formatter, and unit.
    const tooltipContent = useMemo(() => {
        if (showAverageWeightOnly) {
            return makeCustomTooltip({
                formatLabel: utcDateFormatter,
                skipFn: (item) =>
                    "weight" === item.dataKey && !item.strokeWidth,
                showUnit: true,
                formatValue: weightFormatter,
            });
        }
        const tooltipDataSet = dataSet.flatMap((item) => {
            return filterMap(dataDef, (def) =>
                item.hasOwnProperty(def.dataKey)
                    ? {
                          dataKey: def.dataKey,
                          value: item[def.dataKey],
                          name: def.name,
                          unit: def.unit,
                          fill: resolveColorHexByNameWithWCAG(def.color),
                          stroke: def.color,
                          formatter: def.formatter,
                          time: item.time,
                          payload: item,
                      }
                    : null
            );
        });
        return <CompareWeightsTooltip data={tooltipDataSet} />;
        // eslint-disable-next-line
    }, [dataDef, dataSet, showAverageWeightOnly, weightFormatter, darkMode]);

    return (
        <div className={"h-0 mh-15rem"}>
            <Chart
                data={dataSet}
                dataDef={dataDef}
                syncId={"idealChart"}
                isAnimationActive={false}
                tooltipContent={tooltipContent}
                syncMethod={"value"}
                saveAsExcell={t("weighting")}
                Yaxis={{
                    name: t("weight"),
                    yAxisId: "lineAxis",
                    type: "number",
                    domain: chartDomainY,
                    formatter: weightFormatter,
                }}
                thirdYaxis={{
                    yAxisId: "scatterAxis",
                    type: "number",
                    domain: chartDomainY,
                }}
                Xaxis={{
                    name: "",
                    domain: chartDomain,
                    dataKey: "time",
                    formatter: utcDateFormatter,
                    type: "number",
                }}
                type={"Composed"}
                referenceLines={referenceLines}
            />
            {referenceLines && referenceLines.length === 0 && (
                <div className="chart-template-no-data">
                    <h5>{t("chartDisplayNoBirthTime")}</h5>
                    <div>{t("chartDisplayNoBirthTimeText")}</div>
                </div>
            )}
        </div>
    );
}

export default React.memo(WeightingChart);
